<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { UserButton } from '@clerk/vue'
import ProjectSwitcher from './ProjectSwitcher.vue'
import { api } from '../lib/api'
import type { Notification } from '../lib/types'
import { relativeTime } from '@/lib/time'
import { changeRequestsVersion } from '@/lib/approvalNotice'

const route = useRoute()
const router = useRouter()

const appVersion = __APP_VERSION__

// Org switcher
interface ClerkOrg {
  id: string
  name: string
}
const userOrgs = ref<ClerkOrg[]>([])
const currentOrgName = ref('')
const showOrgSwitcher = ref(false)
const orgSwitcherRef = ref<HTMLElement | null>(null)

async function loadUserOrgs() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const clerk = (window as any).Clerk
  if (!clerk?.user) return

  try {
    const memberships = await clerk.user.getOrganizationMemberships()
    userOrgs.value = memberships.data?.map((m: { organization: { id: string; name: string } }) => ({
      id: m.organization.id,
      name: m.organization.name,
    })) || []
  } catch {
    userOrgs.value = []
  }

  if (clerk.organization) {
    currentOrgName.value = clerk.organization.name || 'Organization'
  } else if (userOrgs.value.length > 0) {
    currentOrgName.value = userOrgs.value[0].name
  }
}

async function switchOrg(orgId: string) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const clerk = (window as any).Clerk
  if (!clerk) return

  try {
    await clerk.setActive({ organization: orgId })
    showOrgSwitcher.value = false
    // Go to the overview, not reload(): the current URL may name a project in
    // the org we just left. Still a full load so cached org-scoped state goes.
    window.location.assign(router.resolve({ name: 'DashboardOverview' }).href)
  } catch {
    // switch failed — page will stay on current org
  }
}

// Refresh the org name after a rename: show it right away, then reload Clerk
// (its client cache is stale) to reconcile the switcher list.
async function handleOrgRenamed(e: Event) {
  const detail = (e as CustomEvent<{ name?: string }>).detail
  if (detail?.name) currentOrgName.value = detail.name

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const clerk = (window as any).Clerk
  try {
    await clerk?.organization?.reload?.()
  } catch {
    // best-effort; loadUserOrgs still refreshes below
  }
  await loadUserOrgs()
  // Keep the saved name if Clerk's cache hasn't caught up yet.
  if (detail?.name) currentOrgName.value = detail.name
}

function handleOrgSwitcherClickOutside(e: MouseEvent) {
  if (
    showOrgSwitcher.value &&
    orgSwitcherRef.value &&
    !orgSwitcherRef.value.contains(e.target as Node)
  ) {
    showOrgSwitcher.value = false
  }
}

// Admin access check — probe the admin health endpoint once on mount
const isAdmin = ref(false)

async function checkAdminAccess() {
  try {
    const res = await api.adminHealthCheck()
    isAdmin.value = res
  } catch {
    isAdmin.value = false
  }
}

const currentProject = computed(() => route.params.project as string | undefined)

// Pending change request count for sidebar badge
const pendingCRCount = ref(0)

async function fetchPendingCRCount() {
  if (!currentProject.value) {
    pendingCRCount.value = 0
    return
  }
  try {
    const res = await api.pendingChangeRequestCount(currentProject.value)
    pendingCRCount.value = res.count
  } catch {
    pendingCRCount.value = 0
  }
}

// Refetch on project switch and whenever this tab creates/reviews a change
// request (changeRequestsVersion bumps), so the badge never goes stale.
watch([currentProject, changeRequestsVersion], () => fetchPendingCRCount(), { immediate: true })

interface NavItem {
  label: string
  icon: string
  to: string
}

const navItems = computed<NavItem[]>(() => {
  if (!currentProject.value) return []
  const base = `/dashboard/${currentProject.value}`
  return [
    { label: 'Flags', icon: 'flag', to: base },
    { label: 'Analytics', icon: 'chart', to: `${base}/analytics` },
    { label: 'Environments', icon: 'cloud', to: `${base}/environments` },
    { label: 'Promotions', icon: 'promote', to: `${base}/promotions` },
    { label: 'Segments', icon: 'users', to: `${base}/segments` },
    { label: 'Schedule', icon: 'clock', to: `${base}/schedule` },
    { label: 'Versions', icon: 'history', to: `${base}/versions` },
    { label: 'Audit Log', icon: 'list', to: `${base}/audit-log` },
    { label: 'Approvals', icon: 'approval', to: `${base}/change-requests` },
    { label: 'Settings', icon: 'settings', to: `${base}/settings` },
    { label: 'Playground', icon: 'beaker', to: `${base}/playground` },
  ]
})

function isActive(to: string): boolean {
  return route.path === to
}

function navigate(to: string) {
  router.push(to)
}

const iconMap: Record<string, string> = {
  flag: 'M3 3v18h2V3H3zm4 0v10h10l-3-5 3-5H7z',
  chart: 'M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z',
  cloud: 'M19.35 10.04A7.49 7.49 0 0012 4C9.11 4 6.6 5.64 5.35 8.04A5.994 5.994 0 000 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z',
  users: 'M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z',
  clock: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z',
  history: 'M13 3a9 9 0 00-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42A8.954 8.954 0 0013 21a9 9 0 000-18zm-1 5v5l4.28 2.54.72-1.21-3.5-2.08V8H12z',
  list: 'M3 13h2v-2H3v2zm0 4h2v-2H3v2zm0-8h2V7H3v2zm4 4h14v-2H7v2zm0 4h14v-2H7v2zM7 7v2h14V7H7z',
  settings: 'M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58a.49.49 0 00.12-.61l-1.92-3.32a.488.488 0 00-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54a.484.484 0 00-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.07.62-.07.94s.02.64.07.94l-2.03 1.58a.49.49 0 00-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z',
  promote: 'M9 3L5 6.99h3V14h2V6.99h3L9 3zm7 14.01V10h-2v7.01h-3L15 21l4-3.99h-3z',
  beaker: 'M7 2v2h1v7.15l-4.35 7.61C2.87 20.14 3.84 22 5.44 22h13.12c1.6 0 2.57-1.86 1.79-3.24L16 11.15V4h1V2H7zm2 2h6v7.54l4.06 7.1c.13.23-.03.36-.18.36H5.12c-.15 0-.31-.13-.18-.36L9 11.54V4z',
  approval: 'M18 3h-4.18C13.4 1.84 12.3 1 11 1c-1.3 0-2.4.84-2.82 2H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm-2 14l-4-4 1.41-1.41L9 14.17l6.59-6.59L17 9l-8 8z',
}

// --- Notifications ---
const notifications = ref<Notification[]>([])
const showNotifications = ref(false)
const notifDropdownRef = ref<HTMLElement | null>(null)
const bellRef = ref<HTMLElement | null>(null)
let pollInterval: ReturnType<typeof setInterval> | null = null

const unreadCount = computed(() => notifications.value.filter(n => !n.read).length)

async function fetchNotifications() {
  try {
    notifications.value = await api.listNotifications({ limit: 20 })
  } catch {
    // silently ignore - notifications are non-critical
  }
}

function toggleNotifications() {
  showNotifications.value = !showNotifications.value
}

async function markRead(notif: Notification) {
  if (notif.read) return
  notif.read = true
  try {
    await api.markNotificationRead(notif.id)
  } catch {
    notif.read = false
  }
}

async function markAllRead() {
  const prev = notifications.value.map(n => ({ ...n }))
  notifications.value.forEach(n => { n.read = true })
  try {
    await api.markAllNotificationsRead()
  } catch {
    notifications.value = prev as Notification[]
  }
}

function handleClickOutside(e: MouseEvent) {
  if (
    showNotifications.value &&
    notifDropdownRef.value &&
    bellRef.value &&
    !notifDropdownRef.value.contains(e.target as Node) &&
    !bellRef.value.contains(e.target as Node)
  ) {
    showNotifications.value = false
  }
}

onMounted(() => {
  fetchNotifications()
  pollInterval = setInterval(fetchNotifications, 30000)
  document.addEventListener('click', handleClickOutside)
  document.addEventListener('click', handleOrgSwitcherClickOutside)
  window.addEventListener('flagdrop:org-renamed', handleOrgRenamed)
  checkAdminAccess()
  loadUserOrgs()
})

onUnmounted(() => {
  if (pollInterval) clearInterval(pollInterval)
  document.removeEventListener('click', handleClickOutside)
  document.removeEventListener('click', handleOrgSwitcherClickOutside)
  window.removeEventListener('flagdrop:org-renamed', handleOrgRenamed)
})
</script>

<template>
  <aside class="w-[220px] h-full bg-surface-lowest flex flex-col">
    <!-- Logo -->
    <div class="px-md py-lg flex items-center gap-sm">
      <img src="/favicon.svg" alt="FlagDrop" class="w-7 h-7" />
      <span class="font-display text-heading font-bold text-text-primary">FlagDrop</span>
    </div>

    <!-- Org Switcher -->
    <div v-if="userOrgs.length > 1" class="px-sm mb-sm relative" ref="orgSwitcherRef">
      <button
        class="w-full flex items-center gap-sm px-sm py-[8px] rounded-md text-body-sm font-body bg-surface-container hover:bg-surface-container-high transition-colors duration-short text-text-primary"
        @click="showOrgSwitcher = !showOrgSwitcher"
      >
        <svg class="w-4 h-4 flex-shrink-0 text-text-muted" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z" />
        </svg>
        <span class="truncate flex-1 text-left">{{ currentOrgName }}</span>
        <svg class="w-3 h-3 flex-shrink-0 text-text-muted" viewBox="0 0 24 24" fill="currentColor">
          <path d="M7 10l5 5 5-5z" />
        </svg>
      </button>

      <!-- Dropdown -->
      <div
        v-if="showOrgSwitcher"
        class="absolute left-0 right-0 top-full mt-xs rounded-lg border border-outline/15 bg-surface-lowest/95 backdrop-blur-md shadow-lg z-50 max-h-[200px] overflow-y-auto"
      >
        <button
          v-for="org in userOrgs"
          :key="org.id"
          class="w-full text-left px-sm py-sm text-body-sm font-body transition-colors duration-short hover:bg-surface-container-high"
          :class="org.name === currentOrgName ? 'text-primary' : 'text-text-secondary'"
          @click="switchOrg(org.id)"
        >
          {{ org.name }}
        </button>
      </div>
    </div>

    <!-- Project Switcher -->
    <div class="px-sm mb-sm">
      <ProjectSwitcher />
    </div>

    <!-- Navigation -->
    <nav class="flex-1 px-sm overflow-y-auto">
      <!-- Overview link (always visible) -->
      <button
        class="w-full flex items-center gap-sm px-sm py-[10px] rounded-md text-body-sm font-body transition-colors duration-short"
        :class="route.path === '/dashboard' && !currentProject
          ? 'bg-surface-container-high text-text-primary'
          : 'text-text-secondary hover:text-text-primary hover:bg-surface-container'"
        @click="navigate('/dashboard')"
      >
        <svg class="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
          <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z" />
        </svg>
        <span>Overview</span>
      </button>

      <!-- Organization link (always visible) -->
      <button
        class="w-full flex items-center gap-sm px-sm py-[10px] rounded-md text-body-sm font-body transition-colors duration-short"
        :class="route.path === '/organization'
          ? 'bg-surface-container-high text-text-primary'
          : 'text-text-secondary hover:text-text-primary hover:bg-surface-container'"
        @click="navigate('/organization')"
      >
        <svg class="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z" />
        </svg>
        <span>Organization</span>
      </button>

      <!-- Billing link (always visible) -->
      <button
        class="w-full flex items-center gap-sm px-sm py-[10px] rounded-md text-body-sm font-body transition-colors duration-short"
        :class="route.path === '/billing'
          ? 'bg-surface-container-high text-text-primary'
          : 'text-text-secondary hover:text-text-primary hover:bg-surface-container'"
        @click="navigate('/billing')"
      >
        <svg class="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
          <path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z" />
        </svg>
        <span>Billing</span>
      </button>

      <!-- Project nav items (visible when a project is selected) -->
      <template v-if="currentProject">
        <div class="mt-md mb-xs">
          <span class="section-label px-sm">Project</span>
        </div>
        <button
          v-for="item in navItems"
          :key="item.to"
          class="w-full flex items-center gap-sm px-sm py-[10px] rounded-md text-body-sm font-body transition-colors duration-short"
          :class="isActive(item.to)
            ? 'bg-surface-container-high text-text-primary'
            : 'text-text-secondary hover:text-text-primary hover:bg-surface-container'"
          @click="navigate(item.to)"
        >
          <svg class="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
            <path :d="iconMap[item.icon]" />
          </svg>
          <span>{{ item.label }}</span>
          <span
            v-if="item.label === 'Approvals' && pendingCRCount > 0"
            class="ml-auto flex items-center justify-center min-w-[18px] h-[18px] px-[4px] rounded-full bg-warning text-surface-lowest text-[10px] font-bold leading-none"
          >
            {{ pendingCRCount > 99 ? '99+' : pendingCRCount }}
          </span>
        </button>
      </template>
    </nav>

    <!-- Admin link (visible only for admin org) -->
    <div v-if="isAdmin" class="px-sm mb-sm">
      <button
        class="w-full flex items-center gap-sm px-sm py-[10px] rounded-md text-body-sm font-body transition-colors duration-short text-warning hover:text-text-primary hover:bg-surface-container"
        @click="navigate('/admin')"
      >
        <svg class="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z" />
        </svg>
        <span>Admin</span>
      </button>
    </div>

    <!-- Notifications + User section -->
    <div class="px-md py-md border-t border-outline/15">
      <!-- Bell icon -->
      <div class="relative mb-sm">
        <button
          ref="bellRef"
          class="relative flex items-center gap-sm px-sm py-[6px] rounded-md text-text-muted hover:text-text-primary hover:bg-surface-container transition-colors duration-short w-full"
          @click="toggleNotifications"
        >
          <svg class="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z" />
          </svg>
          <span class="text-body-sm font-body">Notifications</span>
          <span
            v-if="unreadCount > 0"
            class="absolute top-0 left-3 flex items-center justify-center min-w-[16px] h-4 px-[4px] rounded-full bg-error text-white text-[10px] font-bold leading-none"
          >
            {{ unreadCount > 99 ? '99+' : unreadCount }}
          </span>
        </button>

        <!-- Dropdown -->
        <div
          v-if="showNotifications"
          ref="notifDropdownRef"
          class="fixed sm:absolute bottom-0 left-0 sm:bottom-full sm:left-0 sm:mb-xs w-full sm:w-[300px] max-h-[70vh] sm:max-h-[360px] overflow-y-auto rounded-t-lg sm:rounded-lg border border-outline/15 bg-surface-lowest/95 backdrop-blur-md shadow-lg z-50"
        >
          <!-- Header -->
          <div class="flex items-center justify-between px-sm py-sm border-b border-outline/15">
            <span class="text-body-sm font-body font-semibold text-text-primary">Notifications</span>
            <button
              v-if="unreadCount > 0"
              class="text-[11px] text-primary hover:text-primary-light transition-colors"
              @click="markAllRead"
            >
              Mark all read
            </button>
          </div>

          <!-- Notification list -->
          <div v-if="notifications.length === 0" class="px-sm py-lg text-center text-text-muted text-body-sm">
            No notifications yet
          </div>
          <button
            v-for="notif in notifications"
            :key="notif.id"
            class="w-full text-left px-sm py-sm flex gap-sm items-start transition-colors duration-short"
            :class="notif.read ? 'hover:bg-surface-container' : 'bg-surface-container-high hover:bg-surface-container-highest'"
            @click="markRead(notif)"
          >
            <!-- Type indicator dot -->
            <span class="mt-[5px] flex-shrink-0 w-2 h-2 rounded-full" :class="{
              'bg-primary': notif.type === 'info',
              'bg-warning': notif.type === 'warning',
              'bg-error': notif.type === 'error',
            }" />
            <div class="flex-1 min-w-0">
              <p class="text-body-sm text-text-primary leading-snug" :class="{ 'font-medium': !notif.read }">
                {{ notif.message }}
              </p>
              <p class="text-[11px] text-text-muted mt-[2px]">
                {{ relativeTime(notif.createdAt) }}
              </p>
            </div>
          </button>
        </div>
      </div>

      <UserButton />
      <p class="font-mono text-[10px] text-text-muted/50 mt-sm">v{{ appVersion }}</p>
    </div>
  </aside>
</template>
